import Janus from 'janus-gateway';

let janusReady = false;

function ensureJanus (onReady, onError) {
	if (janusReady) {
		onReady();
		return;
	}
	if (!Janus.isWebrtcSupported()) {
		onError('WebRTC is not supported in this browser.');
		return;
	}
	Janus.init({
		debug: process.env.NODE_ENV === 'development' ? 'all' : false,
		callback: () => {
			janusReady = true;
			onReady();
		},
	});
}

/**
 * Description JSON published by Android `VideoConsultMetadata`. Anything that
 * does not parse or doesn't carry `type === "video-consult"` is filtered out
 * by the discovery list — keeps unrelated VideoRooms (test rooms, the legacy
 * `1234` appointment room, etc) from polluting the consult picker.
 *
 * @typedef {Object} ConsultMetadata
 * @property {number} schema
 * @property {"video-consult"} type
 * @property {number} createdAt
 * @property {{ id?: number, name?: string }} [operator]
 * @property {{ id?: number, name?: string }} [patient]
 * @property {{ id?: number, name?: string }} [hospital]
 * @property {{ versionName?: string, versionCode?: number }} [app]
 */

/**
 * @typedef {Object} ConsultRoom
 * @property {number} room                Janus room id
 * @property {string} descriptionRaw      Raw `description` string Janus returned
 * @property {ConsultMetadata | null} metadata  Parsed JSON or null
 * @property {number} numParticipants
 */

export function parseConsultDescription (raw) {
	if (!raw || typeof raw !== 'string') return null;
	try {
		const obj = JSON.parse(raw);
		if (!obj || obj.type !== 'video-consult') return null;
		return obj;
	} catch {
		return null;
	}
}

/**
 * Background poller that calls VideoRoom `list` on a single discovery handle
 * and forwards parsed consult rooms to the caller. Only consult rooms (those
 * whose JSON description has `type === "video-consult"`) are surfaced.
 */
export class ConsultDiscovery {
	/**
	 * @param {(rooms: ConsultRoom[]) => void} onRooms
	 * @param {(line: string, level?: "info" | "warn" | "error") => void} onLog
	 */
	constructor (onRooms, onLog) {
		this.onRooms = onRooms;
		this.onLog = onLog;

		this.janus = null;
		this.handle = null;
		this.pollTimer = null;
		this.pollMs = 2500;
		this.pendingList = false;
	}

	/**
	 * @param {string} serverUrl
	 * @param {number} [pollMs]
	 */
	start (serverUrl, pollMs = 2500) {
		this.stop();
		this.pollMs = Math.max(750, pollMs | 0);
		ensureJanus(
			() => this.openSession(serverUrl),
			(err) => this.onLog('Discovery: ' + err, 'error'),
		);
	}

	stop () {
		if (this.pollTimer) {
			clearInterval(this.pollTimer);
			this.pollTimer = null;
		}
		try {
			if (this.handle) this.handle.detach();
		} catch {
			/* ignore */
		}
		this.handle = null;
		if (this.janus) {
			try {
				this.janus.destroy();
			} catch {
				/* ignore */
			}
			this.janus = null;
		}
	}

	openSession (serverUrl) {
		this.janus = new Janus({
			server: serverUrl,
			success: () => this.attachListHandle(),
			error: (err) => this.onLog('Discovery session error: ' + err, 'error'),
			destroyed: () => {
				/* expected on stop() */
			},
		});
	}

	attachListHandle () {
		if (!this.janus) return;
		this.janus.attach({
			plugin: 'janus.plugin.videoroom',
			opaqueId: 'consult-discovery',
			success: (handle) => {
				this.handle = handle;
				this.requestList();
				this.pollTimer = setInterval(() => this.requestList(), this.pollMs);
			},
			error: (err) => this.onLog('Discovery attach: ' + err, 'error'),
			onmessage: () => {
				/* responses arrive via send().success */
			},
		});
	}

	/**
	 * Ask Janus to destroy a VideoRoom. Uses the shared discovery handle so
	 * we don't need to create a brand-new Janus session just for this.
	 *
	 * Resolves with `{ ok: true, roomId }` on success, or
	 * `{ ok: false, error }` on failure. The next discovery poll will drop
	 * the room from the list automatically.
	 *
	 * @param {number} roomId
	 * @returns {Promise<{ok: boolean, roomId: number, error?: string}>}
	 */
	destroyRoom (roomId, timeoutMs = 8000) {
		const id = Number(roomId);
		const ms = Math.max(1000, timeoutMs | 0);

		return new Promise((resolve) => {
			let settled = false;
			const finish = (result) => {
				if (settled) return;
				settled = true;
				clearTimeout(timer);
				resolve(result);
			};

			const timer = setTimeout(() => {
				this.onLog(`Destroy room #${id} timed out after ${ms}ms (Janus did not respond).`, 'warn');
				finish({ ok: false, roomId: id, error: 'timeout' });
			}, ms);

			if (!this.handle) {
				finish({ ok: false, roomId: id, error: 'Discovery not running' });
				return;
			}
			if (!Number.isFinite(id) || id <= 0) {
				finish({ ok: false, roomId: id, error: 'Invalid room id' });
				return;
			}
			this.onLog(`Destroying room #${id}…`, 'info');
			this.handle.send({
				message: { request: 'destroy', room: id },
				success: (response) => {
					// Force the list to refresh now so the row disappears immediately
					// instead of waiting for the next poll tick.
					this.pendingList = false;
					this.requestList();
					this.onLog(`Room #${id} destroyed.`, 'info');
					finish({ ok: true, roomId: id, response });
				},
				error: (err) => {
					const msg = typeof err === 'string' ? err : (err && err.error) || 'destroy failed';
					this.onLog(`Destroy room #${id} failed: ${msg}`, 'warn');
					// Room may already be gone (operator ended first) — still refresh list.
					this.pendingList = false;
					this.requestList();
					finish({ ok: false, roomId: id, error: msg });
				},
			});
		});
	}

	requestList () {
		if (!this.handle || this.pendingList) return;
		this.pendingList = true;
		this.handle.send({
			message: { request: 'list' },
			success: (response) => {
				this.pendingList = false;
				const list = Array.isArray(response && response.list) ? response.list : [];
				const consults = [];
				const skipped = [];
				for (const r of list) {
					const meta = parseConsultDescription(r.description);
					if (!meta) {
						skipped.push({
							room: r.room,
							description: typeof r.description === 'string' ? r.description.slice(0, 80) : '(none)',
						});
						continue;
					}
					consults.push({
						room: Number(r.room),
						descriptionRaw: r.description,
						metadata: meta,
						numParticipants: r.num_participants != null ? r.num_participants : 0,
					});
				}
				// First poll-cycle log lines so the user can see whether Janus list is
				// returning anything at all and why non-consult rooms are skipped.
				if (this._lastSummary !== `${list.length}/${consults.length}`) {
					this._lastSummary = `${list.length}/${consults.length}`;
					this.onLog(`Discovery: Janus returned ${list.length} rooms, ${consults.length} are consults.`, 'info');
					if (consults.length === 0 && skipped.length > 0) {
						this.onLog(
							`Discovery: skipped rooms — first 5: ${skipped
								.slice(0, 5)
								.map((s) => `#${s.room}(${s.description})`)
								.join(', ')}`,
							'info',
						);
					}
				}
				this.onRooms(consults);
			},
			error: (err) => {
				this.pendingList = false;
				this.onLog('Discovery list: ' + err, 'warn');
			},
		});
	}
}
